import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminCompactListShimmer, AdminListPageShimmer } from '../common/Shimmer.jsx';
import ListPagination from '../common/ListPagination.jsx';
import { PAGE_SIZE_OPTIONS } from '../../utils/listPagination.js';
import { getActiveLicenses } from '../../models/index.js';
import { getImagePreviewUrl } from '../../utils/imageUpload.js';
import { searchAddressByPostalCode, formatPostalCode } from '../../utils/postalCode.js';
import { devError } from '../../utils/logger.js';
import { MODAL_ANIMATION_DURATION } from '../../config/constants.js';
import { can } from '../../config/adminPermissions.js';
import AdminClinicDetail from './AdminClinicDetail.jsx';
import SortOrderToggle from '../common/SortOrderToggle.jsx';
import {
  IconCheck,
  IconCheckCircle,
  IconChevronRight,
  IconClipboard,
  IconFileText,
  IconFlask,
  IconRefresh,
} from '../common/AdminIcons.jsx';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import {
  buildBaselineSelectedLicenses,
  buildLicenseDiff,
  applyClinicLicenseDiff,
  applyClinicLicenseCreates,
  buildLicenseSaveAlert,
  hasLicenseApplyFailure,
  syncFormLicensesAfterApply,
  toLicenseDateInputValue,
} from '../../utils/clinicLicenseSaveUtils.js';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminClinicList.css';

/**
 * クリニック一覧コンポーネント
 */
const AdminClinicList = ({
  clinics: clinicsProp = [],
  users = [],
  clinicStats: clinicStatsProp = {},
  onUpdate,
  serverPagination = false,
  autoOpenCreateSignal = 0,
  onAutoOpenCreateComplete,
  user = null,
}) => {
  const { showAlert } = useAdminDialog();
  const probe = useScreenProbe('clinics');
  const { requestDestructiveAction } = useDestructiveAction();
  const canDelete = can(user, 'clinics:delete');
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDemo, setFilterDemo] = useState('all');
  const [filterDeleted, setFilterDeleted] = useState('active');
  const [sortKey, setSortKey] = useState('registeredAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showModal, setShowModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // フェードアウト中かどうか
  const [editingClinic, setEditingClinic] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logoUrl: '',
    logoFile: null,
    logoPreview: null,
    postalCode: '',
    address: '',
    phone: '',
    email: '',
    contactPerson: '',
    userLimit: null, // null = 無制限
    isDemo: false,
    selectedLicenses: [] // { name, status, trialEndAt } の配列
  });
  const [availableLicenses, setAvailableLicenses] = useState([]); // Firestoreから取得するライセンスリスト
  const [licenseContracts, setLicenseContracts] = useState([]); // 契約情報
  const [freeDaysInputs, setFreeDaysInputs] = useState({});
  const [grantingLicense, setGrantingLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [licenseLimit, setLicenseLimit] = useState(30);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoIsClosing, setDemoIsClosing] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoListLoading, setDemoListLoading] = useState(false);
  const [demoList, setDemoList] = useState([]);
  const [demoCreateCount, setDemoCreateCount] = useState(1);
  const [selectedDemoIds, setSelectedDemoIds] = useState([]);
  // 作成完了モーダル用のステート
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultIsClosing, setResultIsClosing] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // 確認モーダル用ステート
  const [showDemoCreateConfirm, setShowDemoCreateConfirm] = useState(false);
  const [confirmActionError, setConfirmActionError] = useState('');
  const [clinicDeleteLoading, setClinicDeleteLoading] = useState(false);
  
  const adminRepo = AdminRepository.getInstance();

  const [serverClinics, setServerClinics] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalClinics, setTotalClinics] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const debouncedSearch = useDebouncedValue(searchTerm, 400);

  const filterSignature = `${debouncedSearch}|${filterDemo}|${filterDeleted}|${sortKey}|${sortOrder}|${pageSize}`;
  const lastFilterSignatureRef = useRef(filterSignature);

  const clinics = serverPagination ? serverClinics : clinicsProp;

  const buildListParams = useCallback((page = 1) => ({
    search: debouncedSearch,
    page,
    limit: pageSize,
    filterDemo,
    filterDeleted,
    sortKey,
    sortOrder,
  }), [debouncedSearch, pageSize, filterDemo, filterDeleted, sortKey, sortOrder]);

  const applyPageData = useCallback((data, pageFallback = 1) => {
    const clinics = data.clinics || [];
    setServerClinics(clinics);
    const total = Number(data.total);
    setTotalClinics(Number.isFinite(total) ? total : clinics.length);
    const pages = Number(data.totalPages);
    const safeTotal = Number.isFinite(total) ? total : clinics.length;
    setTotalPages(
      Number.isFinite(pages)
        ? pages
        : Math.max(1, Math.ceil(safeTotal / pageSize))
    );
    setCurrentPage(data.page ?? pageFallback);
    setHasLoadedOnce(true);
  }, [pageSize]);

  const mergeListStatsFromBatch = useCallback((clinics, statsMap = {}) => {
    return clinics.map((clinic) => {
      if (clinic.listStats && typeof clinic.listStats.userCount === 'number') {
        return clinic;
      }
      const batch = statsMap[clinic.id];
      if (batch) {
        return {
          ...clinic,
          listStats: {
            userCount: batch.userStats?.total ?? 0,
            licenseUsed: batch.licenseStats?.used ?? 0,
            hasExpiredLicense: batch.licenseStats?.hasExpiredLicense === true,
          },
        };
      }
      const licenseUsed = getActiveLicenses(clinic.licenses || {}, licenseLimit).length;
      return {
        ...clinic,
        listStats: {
          userCount: clinic.listSummary?.userCount ?? 0,
          licenseUsed: clinic.listSummary?.licenseUsed ?? licenseUsed,
          hasExpiredLicense: clinic.listSummary?.hasExpiredLicense === true,
        },
      };
    });
  }, [licenseLimit]);

  const clinicPageReqIdRef = useRef(0);

  const fetchClinicPage = useCallback(async (page = 1, { forceRefresh = false } = {}) => {
    if (!serverPagination) return;
    const params = buildListParams(page);
    const cached = !forceRefresh ? adminRepo.peekClinicListCache(params) : null;

    if (cached && !forceRefresh) {
      // 進行中のネットワーク取得を無効化してからキャッシュを適用
      clinicPageReqIdRef.current += 1;
      applyPageData(cached, page);
      setListLoading(false);
      probe.unlock('clinics 表示可能（cache）');
      return;
    }

    const reqId = ++clinicPageReqIdRef.current;
    probe.start('list');
    setListLoading(true);

    try {
      let data = await adminRepo.listClinicsPaginated(params, { forceRefresh });
      if (reqId !== clinicPageReqIdRef.current) return;

      let clinics = data.clinics || [];
      const needsBatchStats = clinics.some(
        (c) => !c.listStats || typeof c.listStats.userCount !== 'number'
      );
      if (needsBatchStats && clinics.length > 0) {
        const stats = await adminRepo.fetchClinicStatsBatch(clinics.map((c) => c.id));
        if (reqId !== clinicPageReqIdRef.current) return;
        clinics = mergeListStatsFromBatch(clinics, stats);
        data = { ...data, clinics };
      } else if (needsBatchStats) {
        clinics = mergeListStatsFromBatch(clinics, {});
        data = { ...data, clinics };
      }
      applyPageData(data, page);
      probe.end('list', 'ok');
      probe.unlock();
    } catch (err) {
      if (reqId !== clinicPageReqIdRef.current) return;
      probe.end('list', `error: ${err?.message || 'unknown'}`);
      devError('クリニック一覧取得エラー:', err);
      await showAlert({ message: err.message || '一覧の取得に失敗しました', variant: 'error', title: 'エラー' });
    } finally {
      if (reqId === clinicPageReqIdRef.current) {
        setListLoading(false);
      }
    }
  }, [
    adminRepo,
    applyPageData,
    buildListParams,
    mergeListStatsFromBatch,
    serverPagination,
    probe,
    showAlert,
  ]);

  useEffect(() => {
    if (!serverPagination) return;

    let pageToFetch = currentPage;
    if (lastFilterSignatureRef.current !== filterSignature) {
      lastFilterSignatureRef.current = filterSignature;
      pageToFetch = 1;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    fetchClinicPage(pageToFetch);
  }, [serverPagination, currentPage, filterSignature, fetchClinicPage]);

  const detailClinicStats = useMemo(() => {
    if (!serverPagination) return clinicStatsProp;
    const map = { ...clinicStatsProp };
    for (const clinic of serverClinics) {
      if (clinic.listStats) {
        map[clinic.id] = {
          userStats: { total: clinic.listStats.userCount ?? 0, byRole: {} },
          licenseStats: {
            used: clinic.listStats.licenseUsed ?? 0,
            hasExpiredLicense: clinic.listStats.hasExpiredLicense === true,
            available: Math.max(0, licenseLimit - (clinic.listStats.licenseUsed ?? 0)),
          },
        };
      }
    }
    return map;
  }, [serverPagination, clinicStatsProp, serverClinics, licenseLimit]);

  const clinicStats = serverPagination ? detailClinicStats : clinicStatsProp;

  const resolveClinicRowStats = useCallback((clinic) => {
    const fromList = clinic.listStats || clinic.listSummary;
    const fromMap = clinicStats[clinic.id] || {};

    const userCount = typeof fromList?.userCount === 'number'
      ? fromList.userCount
      : (fromMap.userStats?.total ?? 0);

    let licenseUsed = typeof fromList?.licenseUsed === 'number'
      ? fromList.licenseUsed
      : fromMap.licenseStats?.used;
    if (typeof licenseUsed !== 'number') {
      licenseUsed = getActiveLicenses(clinic.licenses || {}, licenseLimit).length;
    }

    const hasExpiredLicense = fromList?.hasExpiredLicense === true
      || fromMap.licenseStats?.hasExpiredLicense === true;

    return {
      userStats: { total: userCount },
      licenseStats: { used: licenseUsed, hasExpiredLicense },
    };
  }, [clinicStats, licenseLimit]);

  // 利用可能なライセンスリストを取得
  useEffect(() => {
    loadAvailableLicenses();
  }, []);

  const loadAvailableLicenses = async () => {
    setLoadingLicenses(true);
    try {
      // license-config を正とし、登録済みライセンスは製品の公開設定に関わらず付与可能にする。
      // 製品側のステージング/非公開設定は利用者向け表示のためのもので、管理者のライセンス付与は制限しない。
      const [licenses, config] = await Promise.all([
        adminRepo.getAvailableLicenses(),
        adminRepo.getLicenseConfig(),
      ]);

      // API 側でも一意化しているが、描画 key の重複警告を確実に防ぐ
      setAvailableLicenses([...new Set(licenses)]);
      setLicenseLimit(config.limit);
    } catch (err) {
      devError('ライセンスリスト取得エラー:', err);
      await showAlert({ message: 'ライセンスリストの取得に失敗しました。', variant: 'error', title: 'エラー' });
    } finally {
      setLoadingLicenses(false);
    }
  };

  const toTimestampMs = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    if (typeof value?._seconds === 'number') return value._seconds * 1000;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const formatDate = (value) => {
    const ms = toTimestampMs(value);
    if (!ms) return '未設定';
    return new Date(ms).toLocaleDateString('ja-JP');
  };

  const displayClinics = useMemo(() => {
    if (serverPagination) return clinics;
    const normalized = searchTerm.toLowerCase();
    const filtered = clinics.filter((clinic) => {
      const matchesSearch =
        clinic.name?.toLowerCase().includes(normalized) ||
        clinic.id?.toLowerCase().includes(normalized);
      if (!matchesSearch) return false;
      if (filterDeleted === 'deleted' ? clinic.isDeleted !== true : clinic.isDeleted === true) {
        return false;
      }
      if (filterDemo === 'demo') return clinic.isDemo === true;
      if (filterDemo === 'non-demo') return !clinic.isDemo;
      return true;
    });
    const direction = sortOrder === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const statsA = clinicStats[a.id] || {};
      const statsB = clinicStats[b.id] || {};
      const userCountA = statsA.userStats?.total || 0;
      const userCountB = statsB.userStats?.total || 0;
      const licenseCountA = statsA.licenseStats?.used || 0;
      const licenseCountB = statsB.licenseStats?.used || 0;
      const expiredA = statsA.licenseStats?.hasExpiredLicense ? 1 : 0;
      const expiredB = statsB.licenseStats?.hasExpiredLicense ? 1 : 0;
      const createdAtA = toTimestampMs(a.createdAt);
      const createdAtB = toTimestampMs(b.createdAt);

      switch (sortKey) {
        case 'name':
          return direction * (a.name || '').localeCompare((b.name || ''), 'ja');
        case 'userCount':
          return direction * (userCountA - userCountB);
        case 'licenseCount':
          return direction * (licenseCountA - licenseCountB);
        case 'hasExpiredLicense':
          return direction * (expiredA - expiredB);
        case 'registeredAt':
        default:
          return direction * (createdAtA - createdAtB);
      }
    });
  }, [serverPagination, clinics, searchTerm, filterDemo, filterDeleted, sortKey, sortOrder, clinicStats]);

  const getClinicReportRows = () => displayClinics.map((clinic) => {
    const { userStats, licenseStats } = resolveClinicRowStats(clinic);
    return {
      registeredAt: formatDate(clinic.createdAt),
      name: clinic.name || '',
      userCount: userStats.total || 0,
      licenseCount: licenseStats.used || 0,
      hasExpiredLicense: licenseStats.hasExpiredLicense ? 'あり' : 'なし',
    };
  });

  const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  const handleExportPdf = async () => {
    setExportLoading(true);
    try {
      let rows;
      if (serverPagination) {
        const data = await adminRepo.exportClinicList({
          search: debouncedSearch,
          filterDemo,
          filterDeleted,
          sortKey,
          sortOrder,
        });
        rows = (data.rows || []).map((row) => ({
          registeredAt: formatDate(row.registeredAt),
          name: row.name || '',
          userCount: row.userCount ?? 0,
          licenseCount: row.licenseCount ?? 0,
          hasExpiredLicense: row.hasExpiredLicense ? 'あり' : 'なし',
        }));
      } else {
        rows = getClinicReportRows();
      }

      if (rows.length === 0) {
        await showAlert({ message: '出力対象のクリニックがありません。', variant: 'error', title: 'PDF出力' });
        return;
      }

    const popup = window.open('', '_blank', 'width=1200,height=800');
    if (!popup) {
      await showAlert({ message: 'PDF出力ウィンドウを開けませんでした。ポップアップブロックを解除してください。', variant: 'error', title: 'PDF出力' });
      return;
    }

    const generatedAt = new Date().toLocaleString('ja-JP');
    const tableRows = rows.map((row) => `
      <tr>
        <td>${escapeHtml(row.registeredAt)}</td>
        <td>${escapeHtml(row.name)}</td>
        <td class="num">${escapeHtml(row.userCount)}</td>
        <td class="num">${escapeHtml(row.licenseCount)}</td>
        <td>${escapeHtml(row.hasExpiredLicense)}</td>
      </tr>
    `).join('');

    popup.document.open();
    popup.document.write(`
      <!doctype html>
      <html lang="ja">
        <head>
          <meta charset="utf-8" />
          <title>クリニック一覧</title>
          <style>
            body { font-family: "Yu Gothic UI", "Meiryo", sans-serif; margin: 24px; color: #111827; }
            h1 { margin: 0 0 8px; font-size: 22px; }
            .meta { margin: 0 0 16px; color: #4b5563; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
            th { background: #f3f4f6; }
            td.num { text-align: right; }
            @media print { body { margin: 12mm; } }
          </style>
        </head>
        <body>
          <h1>クリニック一覧</h1>
          <p class="meta">出力日時: ${escapeHtml(generatedAt)} / 件数: ${rows.length}件</p>
          <table>
            <thead>
              <tr>
                <th>登録日</th>
                <th>名前</th>
                <th>ユーザー数</th>
                <th>ライセンス数</th>
                <th>ライセンス切れ有無</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `);
    popup.document.close();
    popup.onload = () => {
      popup.print();
    };
    } catch (err) {
      devError('PDF出力エラー:', err);
      await showAlert({ message: err.message || 'PDF出力に失敗しました', variant: 'error', title: 'PDF出力' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setFormData({ 
      name: '', 
      logoUrl: '', 
      logoFile: null,
      logoPreview: null,
      postalCode: '',
      address: '',
      phone: '',
      email: '',
      contactPerson: '',
      userLimit: null,
      isDemo: false,
      selectedLicenses: [] 
    });
    setLicenseContracts([]);
    setEditingClinic(null);
    setShowModal(true);
  };

  useEffect(() => {
    if (!autoOpenCreateSignal) return;
    setSelectedClinicId(null);
    handleOpenCreate();
    onAutoOpenCreateComplete?.();
  }, [autoOpenCreateSignal]);

  const handleOpenDemoCreate = () => {
    setShowDemoModal(true);
    setDemoCreateCount(1);
  };

  useEffect(() => {
    if (showDemoModal) {
      loadDemoList();
    }
  }, [showDemoModal]);


  const handleOpenEdit = async (clinic) => {
    // 契約情報を取得し、スロット＋契約のみライセンスを同一ロジックで構築
    let contracts = [];
    try {
      contracts = await adminRepo.getClinicLicenseContracts(clinic.id);
      setLicenseContracts(contracts);
    } catch (err) {
      devError('契約情報取得エラー:', err);
    }

    const selectedLicenses = buildBaselineSelectedLicenses({
      clinicLicenses: clinic.licenses || {},
      licenseLimit,
      contracts,
      getActiveLicenses,
    });

    setFormData({
      name: clinic.name || '',
      logoUrl: clinic.logoUrl || '',
      logoFile: null,
      logoPreview: clinic.logoUrl || null,
      postalCode: clinic.postalCode || '',
      address: clinic.address || '',
      phone: clinic.phone || '',
      email: clinic.email || '',
      contactPerson: clinic.contactPerson || '',
      userLimit: clinic.userLimit !== undefined ? clinic.userLimit : null,
      isDemo: clinic.isDemo === true,
      selectedLicenses: selectedLicenses
    });
    setEditingClinic(clinic);
    setFreeDaysInputs({});
    setGrantingLicense(null);
    setShowModal(true);
  };

  const handleGrantFreeDays = async (licenseName) => {
    if (!editingClinic) return;
    const days = Number(freeDaysInputs[licenseName]);
    if (!Number.isFinite(days) || days <= 0 || !Number.isInteger(days)) {
      await showAlert({
        title: '入力不足',
        variant: 'warning',
        message: '1以上の整数日数を入力してください',
      });
      return;
    }
    setGrantingLicense(licenseName);
    try {
      const result = await adminRepo.grantLicenseFreeDays(editingClinic.id, licenseName, days);
      const contracts = await adminRepo.getClinicLicenseContracts(editingClinic.id);
      setLicenseContracts(contracts);
      setFormData((prev) => ({
        ...prev,
        selectedLicenses: prev.selectedLicenses.map((l) => {
          if (l.name !== licenseName) return l;
          const contract = contracts.find((c) => c.licenseName === licenseName);
          if (!contract) return l;
          return {
            ...l,
            status: contract.status || l.status,
            trialEndAt: toLicenseDateInputValue(contract.trialEndAt),
            trialSource: contract.trialSource || null,
            billingDeferralUntil: toLicenseDateInputValue(contract.billingDeferralUntil),
            billingDeferralReason: contract.billingDeferralReason || null,
          };
        }),
      }));
      setFreeDaysInputs((prev) => ({ ...prev, [licenseName]: '' }));
      await showAlert({
        title: '付与完了',
        variant: 'success',
        message: result.message || `無料利用を${days}日付与しました`,
      });
    } catch (err) {
      await showAlert({
        title: '付与失敗',
        variant: 'error',
        message: err.message || '無料利用日数の付与に失敗しました',
      });
    } finally {
      setGrantingLicense(null);
    }
  };

  const handleCloseModal = () => {
    // フェードアウトアニメーション開始
    setIsClosing(true);
    
    // アニメーション完了後にモーダルを閉じる
    setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
      setEditingClinic(null);
      setFormData({ 
        name: '', 
        logoUrl: '', 
        logoFile: null,
        logoPreview: null,
        postalCode: '',
        address: '',
        phone: '',
        email: '',
        contactPerson: '',
        userLimit: null,
        isDemo: false,
        selectedLicenses: [] 
      });
    }, MODAL_ANIMATION_DURATION);
  };

  const handleCloseDemoModal = () => {
    setDemoIsClosing(true);
    setTimeout(() => {
      setShowDemoModal(false);
      setDemoIsClosing(false);
    }, MODAL_ANIMATION_DURATION);
  };

  const handleCloseResultModal = () => {
    setResultIsClosing(true);
    setTimeout(() => {
      setShowResultModal(false);
      setResultIsClosing(false);
      setResultData(null);
      setCopySuccess(false);
    }, MODAL_ANIMATION_DURATION);
  };

  const handleCopyLoginInfo = async () => {
    if (!resultData) return;
    
    let textToCopy = '';
    
    // 区切りは全角「：」（半角コロン+スペースだとコピー時にスペースまで入力されやすい）
    if (resultData.type === 'clinic') {
      textToCopy =
        `クリニック名：${resultData.name}\n` +
        `クリニックコード：${resultData.code || '-'}\n` +
        `クリニックID：${resultData.id}`;
    } else if (resultData.type === 'demo') {
      textToCopy = resultData.created
        .map((item, idx) => (
          `--- ${idx + 1}件目 ---\n` +
          `クリニック名：${item.clinicName}\n` +
          `クリニックコード：${item.code || '-'}\n` +
          `クリニックID：${item.clinicId}\n` +
          `メール：${item.doctor.email}\n` +
          `パスワード：${item.doctor.password}`
        ))
        .join('\n\n');
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // フォールバック: テキストエリアを使ったコピー
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };


  // 郵便番号の入力ハンドラー（自動検索付き）
  const handlePostalCodeChange = async (e) => {
    const formatted = formatPostalCode(e.target.value);
    const normalizedCode = formatted.replace(/[^\d]/g, '');
    
    setFormData({
      ...formData,
      postalCode: formatted
    });

    // 7桁入力されたら自動で住所を検索
    if (normalizedCode.length === 7) {
      setSearchingAddress(true);
      try {
        const result = await searchAddressByPostalCode(formatted);
        
        if (result.success) {
          setFormData(prev => ({
            ...prev,
            postalCode: formatted,
            address: result.address
          }));
        }
        // エラーの場合はユーザーが手動入力できるよう、alertは出さない
      } catch (error) {
        devError('住所検索エラー:', error);
        // エラーが発生してもユーザーは手動入力できる
      } finally {
        setSearchingAddress(false);
      }
    }
  };

  // フォーム内でEnterキーを押した時の処理
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
      
      // 次の入力可能な要素を探す
      const form = e.target.form;
      const inputs = Array.from(form.querySelectorAll('input, select, textarea, button[type="submit"]'));
      const currentIndex = inputs.indexOf(e.target);
      const nextInput = inputs[currentIndex + 1];
      
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const previewUrl = await getImagePreviewUrl(file);
        setFormData({
          ...formData,
          logoFile: file,
          logoPreview: previewUrl
        });
      } catch (error) {
        devError('画像プレビューエラー:', error);
        await showAlert({ message: '画像の読み込みに失敗しました。', variant: 'error', title: 'エラー' });
      }
    }
  };

  const handleAddLicense = async (licenseName) => {
    if (formData.selectedLicenses.length >= licenseLimit) {
      await showAlert({ message: `ライセンスは最大${licenseLimit}個まで登録できます。`, variant: 'error', title: '入力エラー' });
      return;
    }
    const exists = formData.selectedLicenses.some(l => l.name === licenseName);
    if (!exists) {
      setFormData({
        ...formData,
        selectedLicenses: [...formData.selectedLicenses, {
          name: licenseName,
          status: 'active',
          trialEndAt: ''
        }]
      });
    }
  };

  const handleRemoveLicense = (licenseName) => {
    setFormData({
      ...formData,
      selectedLicenses: formData.selectedLicenses.filter(l => l.name !== licenseName)
    });
  };

  const handleLicenseStatusChange = (licenseName, status) => {
    setFormData({
      ...formData,
      selectedLicenses: formData.selectedLicenses.map(l => {
        if (l.name === licenseName) {
          // 試用から本利用に変更した場合、期限をクリア
          const trialEndAt = status === 'trial' ? l.trialEndAt : '';
          // 'active' でなくなるなら支払猶予 (D) もクリア
          const billingDeferralUntil = status === 'active' ? l.billingDeferralUntil : '';
          const billingDeferralReason = status === 'active' ? l.billingDeferralReason : null;
          return { ...l, status, trialEndAt, billingDeferralUntil, billingDeferralReason };
        }
        return l;
      })
    });
  };

  const handleLicenseTrialEndChange = (licenseName, trialEndAt) => {
    setFormData({
      ...formData,
      selectedLicenses: formData.selectedLicenses.map(l => {
        if (l.name === licenseName) {
          return { ...l, trialEndAt };
        }
        return l;
      })
    });
  };

  const handleLicenseDeferralUntilChange = (licenseName, billingDeferralUntil) => {
    setFormData({
      ...formData,
      selectedLicenses: formData.selectedLicenses.map(l => {
        if (l.name === licenseName) {
          return {
            ...l,
            billingDeferralUntil,
            billingDeferralReason: billingDeferralUntil ? (l.billingDeferralReason || 'admin_grant') : null
          };
        }
        return l;
      })
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      await showAlert({ message: 'クリニック名を入力してください。', variant: 'error', title: '入力エラー' });
      return;
    }

    setLoading(true);
    try {
      if (editingClinic) {
        // 更新時：基本情報を更新、ライセンスは個別に処理
        const updateData = {
          name: formData.name,
          postalCode: formData.postalCode,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          contactPerson: formData.contactPerson,
          userLimit: formData.userLimit,
          isDemo: formData.isDemo === true
        };

        // ロゴファイルがある場合は追加
        if (formData.logoFile) {
          updateData.logoFile = formData.logoFile;
        }

        await adminRepo.updateClinic(editingClinic.id, updateData);

        // 基本情報保存後はロゴ再アップロードを防ぐ（ライセンス再試行時）
        if (formData.logoFile) {
          setFormData((prev) => ({ ...prev, logoFile: null }));
        }

        // ライセンスは差分のみ適用（変更なしは API スキップ）。失敗しても継続し最後にサマリ表示。
        const baselineLicenses = buildBaselineSelectedLicenses({
          clinicLicenses: editingClinic.licenses || {},
          licenseLimit,
          contracts: licenseContracts,
          getActiveLicenses,
        });
        const licenseDiff = buildLicenseDiff(baselineLicenses, formData.selectedLicenses);
        const licenseResults = await applyClinicLicenseDiff({
          clinicId: editingClinic.id,
          diff: licenseDiff,
          adminRepo,
        });
        const licenseAlert = buildLicenseSaveAlert({
          baseMessage: 'クリニックを更新しました。',
          results: licenseResults,
        });
        const licenseFailed = hasLicenseApplyFailure(licenseResults);

        // 部分成功後の再保存で誤差分が出ないよう、サーバー状態でベースラインを更新する。
        // getClinicLicenseContracts は既定で失敗時 [] を返すため、ここでは throwOnError で空上書きを防ぐ。
        try {
          const [contracts, freshClinic] = await Promise.all([
            adminRepo.getClinicLicenseContracts(editingClinic.id, { throwOnError: true }),
            adminRepo.getClinic(editingClinic.id),
          ]);
          const serverLicenses = buildBaselineSelectedLicenses({
            clinicLicenses: freshClinic?.licenses || editingClinic.licenses || {},
            licenseLimit,
            contracts,
            getActiveLicenses,
          });
          setLicenseContracts(contracts);
          if (freshClinic) {
            setEditingClinic((prev) => ({
              ...(prev || {}),
              ...freshClinic,
              id: editingClinic.id,
            }));
          }
          setFormData((prev) => ({
            ...prev,
            logoFile: null,
            selectedLicenses: syncFormLicensesAfterApply(
              prev.selectedLicenses,
              serverLicenses,
              licenseResults
            ),
          }));
        } catch (refreshErr) {
          devError('[AdminClinicList] 保存後のライセンス状態再取得エラー:', refreshErr);
        }

        // クリニック情報の更新後に該当クリニックの契約ドリフトを検出・自動修復する
        // （管理画面での手動編集でも Firestore と Stripe が乖離しないようにするため）
        adminRepo.detectLicenseDriftForClinic(editingClinic.id)
          .then((drift) => {
            if (drift.issues?.length > 0) {
              devError('[AdminClinicList] 契約ドリフト検出:', drift);
            }
          })
          .catch(() => { /* non-blocking */ });

        await showAlert(licenseAlert);

        // ライセンス失敗時はフォームを残して再試行可能にする（基本情報は保存済み）
        if (!licenseFailed) {
          handleCloseModal();
        }
      } else {
        // 新規作成
        const clinicData = {
          name: formData.name,
          postalCode: formData.postalCode,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          contactPerson: formData.contactPerson,
          userLimit: formData.userLimit,
          isDemo: formData.isDemo === true
        };

        // ロゴファイルがある場合は追加
        if (formData.logoFile) {
          clinicData.logoFile = formData.logoFile;
        }

        const { clinicId, code } = await adminRepo.createClinic(clinicData);

        // ライセンス追加（失敗しても継続しサマリ表示）
        const licenseResults = await applyClinicLicenseCreates({
          clinicId,
          licenses: formData.selectedLicenses,
          adminRepo,
        });
        if (formData.selectedLicenses.length > 0) {
          const licenseAlert = buildLicenseSaveAlert({
            baseMessage: 'クリニックを作成しました。',
            results: licenseResults,
          });
          await showAlert(licenseAlert);
        }

        // 作成完了モーダルを表示
        setResultData({
          type: 'clinic',
          name: formData.name,
          code: code || '',
          id: clinicId
        });
        setShowResultModal(true);
        handleCloseModal();
      }

      if (serverPagination) {
        await fetchClinicPage(currentPage, { forceRefresh: true });
      }
      onUpdate();
    } catch (err) {
      await showAlert({ message: `エラーが発生しました: ${err.message}`, variant: 'error', title: 'エラー' });
    } finally {
      setLoading(false);
    }
  };

  const loadDemoList = async () => {
    setDemoListLoading(true);
    try {
      const demos = await adminRepo.getDemoClinics();
      setDemoList(demos);
      setSelectedDemoIds([]);
    } catch (err) {
      await showAlert({ message: `デモ一覧の取得に失敗しました: ${err.message}`, variant: 'error', title: 'エラー' });
    } finally {
      setDemoListLoading(false);
    }
  };

  // デモ作成確認モーダルを開く
  const handleOpenDemoCreateConfirm = (e) => {
    e.preventDefault();
    setConfirmActionError('');
    setShowDemoCreateConfirm(true);
  };

  // デモ作成確認モーダルを閉じる
  const handleCloseDemoCreateConfirm = () => {
    setShowDemoCreateConfirm(false);
    setConfirmActionError('');
  };

  // デモ作成を実行
  const handleConfirmDemoCreate = async () => {
    setDemoLoading(true);
    try {
      const count = Number(demoCreateCount) || 1;
      const result = await adminRepo.createDemoClinicAccount(count);

      // 作成完了モーダルを表示
      setResultData({
        type: 'demo',
        count: result.count,
        created: result.created
      });
      handleCloseDemoCreateConfirm();
      setShowResultModal(true);

      await loadDemoList();
      if (serverPagination) {
        await fetchClinicPage(currentPage, { forceRefresh: true });
      }
      onUpdate();
    } catch (err) {
      setConfirmActionError('エラーが発生しました: ' + err.message);
    } finally {
      setDemoLoading(false);
    }
  };

  const toggleDemoSelection = (clinicId) => {
    setSelectedDemoIds((prev) => {
      if (prev.includes(clinicId)) {
        return prev.filter((id) => id !== clinicId);
      }
      return [...prev, clinicId];
    });
  };

  const handleOpenBulkDeleteConfirm = async () => {
    if (!canDelete || selectedDemoIds.length === 0) return;

    const result = await requestDestructiveAction({
      title: 'デモ一括削除',
      message: `選択した ${selectedDemoIds.length} 件のデモクリニックを削除します。30日間は復元でき、期間経過後に完全削除されます。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    setDemoLoading(true);
    setConfirmActionError('');
    try {
      await Promise.all(selectedDemoIds.map((id) => adminRepo.deleteDemoClinic(id, result.reason)));
      setSelectedDemoIds([]);
      await loadDemoList();
      onUpdate?.();
    } catch (err) {
      setConfirmActionError('削除に失敗しました: ' + err.message);
    } finally {
      setDemoLoading(false);
    }
  };

  const handleOpenSingleDeleteConfirm = async (demo) => {
    if (!canDelete) return;
    const result = await requestDestructiveAction({
      title: 'デモ削除',
      message: `デモ「${demo.clinicName || demo.clinicId}」を削除します。30日間は復元でき、期間経過後に完全削除されます。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    setDemoLoading(true);
    setConfirmActionError('');
    try {
      await adminRepo.deleteDemoClinic(demo.clinicId, result.reason);
      await loadDemoList();
      onUpdate?.();
    } catch (err) {
      setConfirmActionError('削除に失敗しました: ' + err.message);
    } finally {
      setDemoLoading(false);
    }
  };

  const handleOpenClinicDeleteConfirm = async (clinic) => {
    if (!canDelete) return;
    const result = await requestDestructiveAction({
      title: 'クリニック削除',
      message: `「${clinic.name || clinic.id}」を削除します。所属ユーザーはログインできなくなります。30日間は復元でき、期間経過後に完全削除されます。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    setClinicDeleteLoading(true);
    try {
      await adminRepo.deleteClinic(clinic.id, result.reason);
      if (clinic.id === selectedClinicId) {
        setSelectedClinicId(null);
      }
      if (serverPagination) {
        await fetchClinicPage(currentPage, { forceRefresh: true });
      }
      onUpdate?.();
    } catch (err) {
      await showAlert({ message: '削除に失敗しました: ' + err.message, variant: 'error', title: 'エラー' });
    } finally {
      setClinicDeleteLoading(false);
    }
  };

  const formatPurgeRemaining = (clinic) => {
    const ms = toTimestampMs(clinic?.purgeAfterAt);
    if (!ms) return '';
    const days = Math.max(0, Math.ceil((ms - Date.now()) / (24 * 60 * 60 * 1000)));
    return `残り約 ${days} 日`;
  };

  const handleRestoreClinic = async (clinic) => {
    if (!canDelete) return;
    const result = await requestDestructiveAction({
      title: 'クリニック復元',
      message: `「${clinic.name || clinic.id}」を復元します。削除時に無効化したユーザーは再びログイン可能になります。理由を入力して復元を確定してください。`,
      confirmLabel: '復元する',
    });
    if (!result?.reason) return;

    setClinicDeleteLoading(true);
    try {
      await adminRepo.restoreClinic(clinic.id, result.reason);
      if (serverPagination) {
        await fetchClinicPage(currentPage, { forceRefresh: true });
      }
      onUpdate?.();
      await showAlert({ message: 'クリニックを復元しました。', variant: 'success', title: '復元完了' });
    } catch (err) {
      await showAlert({ message: '復元に失敗しました: ' + err.message, variant: 'error', title: 'エラー' });
    } finally {
      setClinicDeleteLoading(false);
    }
  };


  const selectedClinic = selectedClinicId
    ? clinics.find((clinic) => clinic.id === selectedClinicId)
    : null;

  const openClinicDetail = (clinicId) => {
    setSelectedClinicId(clinicId);
  };

  if (serverPagination && !hasLoadedOnce && listLoading && !selectedClinicId) {
    return <AdminListPageShimmer variant="clinic" rows={8} />;
  }

  return (
    <div className="admin-list-container">
      <div key={selectedClinicId || 'list'} className="admin-view-panel">
      {selectedClinic ? (
        <AdminClinicDetail
          clinic={selectedClinic}
          clinicStats={clinicStats}
          users={users}
          clinics={clinics}
          licenseLimit={licenseLimit}
          onBack={() => setSelectedClinicId(null)}
          onUpdate={onUpdate}
          onEditClinic={handleOpenEdit}
          user={user}
        />
      ) : (
        <>
      <div className="list-header">
        <div className="section-header">
          <h1>クリニック管理</h1>
          <p>クリニックとライセンスの管理</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleOpenDemoCreate} className="btn btn-secondary">
            <IconFlask size={16} />
            デモ管理
          </button>
          <button onClick={handleOpenCreate} className="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            新規クリニック
          </button>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="search-bar list-toolbar__search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="クリニック名またはIDで検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="admin-select"
          value={filterDemo}
          onChange={(e) => setFilterDemo(e.target.value)}
          aria-label="デモアカウントで絞り込み"
        >
          <option value="all">すべて</option>
          <option value="demo">デモのみ</option>
          <option value="non-demo">デモ以外</option>
        </select>

        <select
          className="admin-select"
          value={filterDeleted}
          onChange={(e) => setFilterDeleted(e.target.value)}
          aria-label="削除状態で絞り込み"
        >
          <option value="active">有効</option>
          <option value="deleted">削除済み（復元可）</option>
        </select>

        <div className="list-toolbar__sort">
          <span className="list-toolbar__sort-label">並び替え</span>
          <select
            id="clinicSortKey"
            className="admin-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            aria-label="並び替え項目"
          >
            <option value="registeredAt">登録日</option>
            <option value="name">名前</option>
            <option value="userCount">ユーザー数</option>
            <option value="licenseCount">ライセンス数</option>
            <option value="hasExpiredLicense">ライセンス切れ</option>
          </select>
          <SortOrderToggle
            order={sortOrder}
            onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          />
        </div>

        <div className="list-toolbar__actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleExportPdf} disabled={exportLoading}>
            <IconFileText size={16} />
            {exportLoading ? '出力中...' : 'PDF出力'}
          </button>
        </div>
      </div>

      <div className="list-result-bar">
        <div className="list-result-bar__meta">
          <span className="list-result-count">
            {serverPagination ? `${totalClinics} 件` : `${displayClinics.length} 件`}
          </span>
          {(searchTerm || filterDemo !== 'all' || filterDeleted !== 'active') && (
            <span className="list-result-hint">フィルタ適用中</span>
          )}
        </div>
        {serverPagination && (
          <ListPagination
            variant="compact"
            currentPage={currentPage}
            totalPages={totalPages}
            total={totalClinics}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            disabled={listLoading}
          />
        )}
      </div>

      {/* クリニック一覧 */}
      <div className="admin-compact-list admin-compact-list--clinics">
        {serverPagination && listLoading ? (
          <AdminCompactListShimmer variant="clinic" rows={8} />
        ) : displayClinics.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            </svg>
            <p>{searchTerm ? '検索結果がありません。' : 'クリニックが登録されていません。'}</p>
          </div>
        ) : (
          <>
            <div className="admin-compact-list-header is-clinic" aria-hidden="true">
              <span className="admin-compact-col-name">クリニック</span>
              <span className="admin-compact-col-metric">ユーザー</span>
              <span className="admin-compact-col-metric">ライセンス</span>
              <span className="admin-compact-col-actions" />
            </div>
            {displayClinics.map((clinic) => {
              const { userStats, licenseStats } = resolveClinicRowStats(clinic);
              const userLabel = clinic.userLimit == null
                ? `${userStats.total} 名`
                : `${userStats.total}/${clinic.userLimit}`;

              return (
                <div
                  key={clinic.id}
                  className={`admin-compact-row admin-compact-row--clinic${licenseStats.hasExpiredLicense ? ' has-warning' : ''}`}
                >
                  <div
                    className="admin-compact-row-main is-clinic"
                    onClick={() => openClinicDetail(clinic.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openClinicDetail(clinic.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${clinic.name} の詳細を開く`}
                  >
                    <div className="admin-compact-col-name">
                      <div className="admin-compact-name-row">
                        {clinic.isDemo && (
                          <span className="badge info admin-compact-inline-badge clinic-demo-badge">デモ</span>
                        )}
                        {clinic.isDeleted && (
                          <span className="badge warning admin-compact-inline-badge">削除済み</span>
                        )}
                        <span className="admin-compact-title">{clinic.name}</span>
                      </div>
                      {clinic.isDeleted && formatPurgeRemaining(clinic) && (
                        <span className="admin-compact-subtext">{formatPurgeRemaining(clinic)}</span>
                      )}
                      {licenseStats.hasExpiredLicense && !clinic.isDeleted && (
                        <span className="badge danger admin-compact-inline-badge">期限切れ</span>
                      )}
                    </div>
                    <div className="admin-compact-col-metric">
                      <span className={`admin-compact-metric${clinic.userLimit != null && userStats.total >= clinic.userLimit ? ' is-danger' : ''}`}>
                        {userLabel}
                      </span>
                    </div>
                    <div className="admin-compact-col-metric">
                      <span className="admin-compact-metric">{licenseStats.used}/{licenseLimit}</span>
                    </div>
                    <div
                      className="admin-compact-col-actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-icon"
                        onClick={() => openClinicDetail(clinic.id)}
                        title="詳細を開く"
                        aria-label={`${clinic.name} の詳細を開く`}
                      >
                        <IconChevronRight size={16} />
                      </button>
                      {clinic.isDeleted ? (
                        canDelete ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => handleRestoreClinic(clinic)}
                          disabled={clinicDeleteLoading}
                        >
                          復元
                        </button>
                        ) : null
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(clinic)}
                            className="btn btn-sm btn-secondary"
                          >
                            編集
                          </button>
                          {canDelete && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleOpenClinicDeleteConfirm(clinic)}
                            disabled={clinicDeleteLoading}
                          >
                            削除
                          </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
        {serverPagination && (
          <ListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            total={totalClinics}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            disabled={listLoading}
          />
        )}

      </div>

        </>
      )}

      {/* 作成/編集モーダル */}
      {showModal && (
        <div className={`modal-overlay ${isClosing ? 'closing' : ''}`}>
          <div className={`modal modal-wide ${isClosing ? 'closing' : ''}`}>
            <div className="modal-header">
              <h3>{editingClinic ? 'クリニック編集' : '新規クリニック作成'}</h3>
              <button onClick={handleCloseModal} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="modal-body">
              <div className="form-group">
                <label htmlFor="name">クリニック名 *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例: 東京デンタルクリニック"
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label clinic-demo-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isDemo === true}
                    onChange={(e) => setFormData({ ...formData, isDemo: e.target.checked })}
                  />
                  <span>デモアカウント</span>
                </label>
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  一覧にデモバッジを表示し、デモ用の識別に利用します
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="postalCode">
                  郵便番号
                  {searchingAddress && <span style={{ marginLeft: '8px', color: 'var(--color-accent-primary)', fontSize: '0.875rem' }}>（住所を検索中...）</span>}
                </label>
                <input
                  id="postalCode"
                  type="text"
                  value={formData.postalCode}
                  onChange={handlePostalCodeChange}
                  placeholder="例: 100-0001"
                  maxLength="8"
                />
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  7桁入力すると自動的に住所を検索します
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="address">住所</label>
                <input
                  id="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="例: 東京都渋谷区〇〇1-2-3"
                />
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  自動入力後、番地等を手動で追記してください
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="phone">電話番号</label>
                <input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="例: 03-1234-5678"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">メールアドレス</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="例: info@clinic.example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactPerson">担当者名</label>
                <input
                  id="contactPerson"
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  placeholder="例: 山田 太郎"
                />
              </div>

              <div className="form-group">
                <label htmlFor="userLimit">ユーザー数上限</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 0 }}>
                    <input
                      type="radio"
                      name="userLimitType"
                      checked={formData.userLimit === null}
                      onChange={() => setFormData({ ...formData, userLimit: null })}
                    />
                    <span>上限なし</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 0 }}>
                    <input
                      type="radio"
                      name="userLimitType"
                      checked={formData.userLimit !== null}
                      onChange={() => setFormData({ ...formData, userLimit: 10 })}
                    />
                    <span>上限を設定:</span>
                  </label>
                  <input
                    id="userLimit"
                    type="number"
                    min="1"
                    value={formData.userLimit || ''}
                    onChange={(e) => setFormData({ ...formData, userLimit: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="例: 10"
                    disabled={formData.userLimit === null}
                    style={{ width: '120px' }}
                  />
                  <span style={{ color: 'var(--color-text-muted)' }}>人</span>
                </div>
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  上限に達すると、新規ユーザーの登録ができなくなります
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="logo">ロゴ画像（任意）</label>
                <input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                {formData.logoPreview && (
                  <div className="image-preview">
                    <img src={formData.logoPreview} alt="ロゴプレビュー" />
                  </div>
                )}
              </div>

              {/* ライセンス管理 */}
              <div className="form-group">
                <label>
                  ライセンス管理 ({formData.selectedLicenses.length}/{licenseLimit})
                  <button 
                    type="button" 
                    onClick={loadAvailableLicenses}
                    className="btn-link btn-link-with-icon"
                    disabled={loadingLicenses}
                  >
                    <IconRefresh size={14} />
                    リストを更新
                  </button>
                </label>

                {/* 選択済みライセンス（状態・期限設定付き） */}
                {formData.selectedLicenses.length > 0 && (
                  <div className="selected-licenses" style={{ marginBottom: '16px' }}>
                    <div className="license-tags-header">選択中のライセンス:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                      {formData.selectedLicenses.map((license) => (
                        <div key={license.name} style={{ 
                          background: 'var(--color-bg-secondary)', 
                          padding: '12px', 
                          borderRadius: '8px',
                          border: '1px solid var(--color-border-light)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 600 }}>{license.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveLicense(license.name)}
                              className="btn btn-sm btn-danger"
                              title="削除"
                            >
                              削除
                            </button>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ marginBottom: 0, fontSize: '0.875rem' }}>状態:</label>
                              <select
                                value={license.status}
                                onChange={(e) => handleLicenseStatusChange(license.name, e.target.value)}
                                style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.875rem' }}
                              >
                                <option value="active">本利用</option>
                                <option value="trial">試用</option>
                                <option value="past_due">支払い遅延</option>
                                <option value="canceled">キャンセル</option>
                              </select>
                            </div>
                            {license.status === 'trial' && (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <label style={{ marginBottom: 0, fontSize: '0.875rem' }}>試用終了日:</label>
                                  <input
                                    type="date"
                                    value={license.trialEndAt}
                                    onChange={(e) => handleLicenseTrialEndChange(license.name, e.target.value)}
                                    style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.875rem' }}
                                  />
                                </div>
                                {license.trialSource && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                    由来: {
                                      license.trialSource === 'invitation_reward' ? '招待特典'
                                      : license.trialSource === 'admin_grant' ? '管理者付与'
                                      : license.trialSource === 'beta' ? 'β版'
                                      : license.trialSource === 'trial_signup' ? '通常試用'
                                      : license.trialSource
                                    }
                                  </div>
                                )}
                              </>
                            )}
                            {license.status === 'active' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ marginBottom: 0, fontSize: '0.875rem' }} title="本契約中の一時的な支払猶予終了日（招待特典など）">
                                  支払猶予終了日:
                                </label>
                                <input
                                  type="date"
                                  value={license.billingDeferralUntil || ''}
                                  onChange={(e) => handleLicenseDeferralUntilChange(license.name, e.target.value)}
                                  style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.875rem' }}
                                />
                                {license.billingDeferralReason && (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                    ({license.billingDeferralReason === 'invitation_reward' ? '招待特典'
                                      : license.billingDeferralReason === 'admin_grant' ? '管理者付与'
                                      : license.billingDeferralReason})
                                  </span>
                                )}
                              </div>
                            )}
                            {editingClinic && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <label style={{ marginBottom: 0, fontSize: '0.875rem' }}>無料利用日数:</label>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={freeDaysInputs[license.name] ?? ''}
                                  onChange={(e) => setFreeDaysInputs((prev) => ({
                                    ...prev,
                                    [license.name]: e.target.value,
                                  }))}
                                  placeholder="日数"
                                  style={{ width: '72px', padding: '4px 8px', borderRadius: '4px', fontSize: '0.875rem' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handleGrantFreeDays(license.name)}
                                  disabled={grantingLicense === license.name}
                                  style={{ fontSize: '0.8125rem', padding: '4px 10px' }}
                                >
                                  {grantingLicense === license.name ? '付与中...' : '日数を付与'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ライセンス追加 */}
                <div className="license-selector">
                  <div className="license-tags-header">追加可能なライセンス:</div>
                  {loadingLicenses ? (
                    <LoadingSpinner size="small" />
                  ) : availableLicenses.length === 0 ? (
                    <p className="text-muted">利用可能なライセンスがありません。</p>
                  ) : (
                    <div className="license-options">
                      {availableLicenses.map((license) => {
                        const isSelected = formData.selectedLicenses.some(l => l.name === license);
                        return (
                        <button
                          key={license}
                          type="button"
                          onClick={() => handleAddLicense(license)}
                          className={`license-option ${isSelected ? 'disabled' : ''}`}
                          disabled={isSelected || formData.selectedLicenses.length >= licenseLimit}
                        >
                          {license}
                          {isSelected && (
                            <span className="badge-with-icon" style={{ marginLeft: '6px' }}>
                              <IconCheck size={12} />
                            </span>
                          )}
                        </button>
                      );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  キャンセル
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <LoadingSpinner size="small" /> : (editingClinic ? '更新' : '作成')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 作成完了モーダル */}
      {showResultModal && resultData && (
        <div className={`modal-overlay ${resultIsClosing ? 'closing' : ''}`} onClick={handleCloseResultModal}>
          <div className={`modal ${resultIsClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title-with-icon">
                <IconCheckCircle size={22} />
                {resultData.type === 'clinic' ? 'クリニック作成完了' : 'デモアカウント作成完了'}
              </h3>
              <button onClick={handleCloseResultModal} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {resultData.type === 'clinic' && (
                <div className="result-info">
                  <div className="result-item">
                    <span className="result-label">クリニック名</span>
                    <span className="result-value">{resultData.name}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">クリニックコード</span>
                    <span className="result-value mono">{resultData.code || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">クリニックID</span>
                    <span className="result-value mono">{resultData.id}</span>
                  </div>
                </div>
              )}

              {resultData.type === 'demo' && (
                <div className="result-info">
                  <p style={{ marginBottom: '16px', color: 'var(--color-text-secondary)' }}>
                    {resultData.count}件のデモアカウントを作成しました
                  </p>
                  {resultData.created.map((item, idx) => (
                    <div key={item.clinicId} className="result-section">
                      <div className="result-section-header">#{idx + 1}</div>
                      <div className="result-item">
                        <span className="result-label">クリニック名</span>
                        <span className="result-value">{item.clinicName}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">クリニックコード</span>
                        <span className="result-value mono">{item.code || '-'}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">クリニックID</span>
                        <span className="result-value mono">{item.clinicId}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">メール</span>
                        <span className="result-value mono">{item.doctor.email}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">パスワード</span>
                        <span className="result-value mono">{item.doctor.password}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={handleCopyLoginInfo} 
                  className={`btn inline-icon-text ${copySuccess ? 'btn-success' : 'btn-secondary'}`}
                >
                  {copySuccess ? (
                    <>
                      <IconCheck size={16} />
                      コピーしました
                    </>
                  ) : (
                    <>
                      <IconClipboard size={16} />
                      ログイン情報をコピー
                    </>
                  )}
                </button>
                <button type="button" onClick={handleCloseResultModal} className="btn btn-primary">
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* デモ作成モーダル */}
      {showDemoModal && (
        <div className={`modal-overlay ${demoIsClosing ? 'closing' : ''}`}>
          <div className={`modal modal-wide ${demoIsClosing ? 'closing' : ''}`}>
            <div className="modal-header">
              <h3>デモ管理</h3>
              <button onClick={handleCloseDemoModal} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleOpenDemoCreateConfirm}>
                <p style={{ marginBottom: '16px' }}>
                  クリニック名・アドレス・医師名は自動採番（16進数連番）で作成されます。
                </p>
                <div className="form-group">
                  <label htmlFor="demoCreateCount">作成数</label>
                  <div className="demo-create-row">
                    <input
                      id="demoCreateCount"
                      type="number"
                      min="1"
                      value={demoCreateCount}
                      onChange={(e) => setDemoCreateCount(e.target.value)}
                    />
                    <button type="submit" className="btn btn-primary" disabled={demoLoading}>
                      {demoLoading ? <LoadingSpinner size="small" /> : '作成'}
                    </button>
                  </div>
                </div>
              </form>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '12px 0' }}>
                <span>登録済みデモ一覧</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={loadDemoList} disabled={demoListLoading}>
                    更新
                  </button>
                  {canDelete && (
                  <button type="button" className="btn btn-sm btn-danger" onClick={handleOpenBulkDeleteConfirm} disabled={demoListLoading || selectedDemoIds.length === 0}>
                    一括削除
                  </button>
                  )}
                </div>
              </div>

              {demoListLoading ? (
                <AdminCompactListShimmer variant="clinic" rows={3} />
              ) : demoList.length === 0 ? (
                <p className="text-muted">デモアカウントがありません。</p>
              ) : (
                <div className="list-content">
                  {demoList.map((demo) => (
                    <div key={demo.clinicId} className="list-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={selectedDemoIds.includes(demo.clinicId)}
                        onChange={() => toggleDemoSelection(demo.clinicId)}
                        style={{ marginTop: '6px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="item-header-with-actions">
                          <div className="item-header">
                            <h3>{demo.clinicName}</h3>
                            <span className="badge info">ID: {demo.clinicId}</span>
                            {demo.code ? (
                              <span className="badge info">Code: {demo.code}</span>
                            ) : null}
                          </div>
                          <div className="item-actions">
                            {canDelete && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleOpenSingleDeleteConfirm(demo)}
                            >
                              削除
                            </button>
                            )}
                          </div>
                        </div>
                        <div className="item-meta">
                          <span className="meta-item">メール: {demo.doctorEmail}</span>
                        </div>
                        <div className="item-meta">
                          <span className="meta-item">パスワード: {demo.doctorPassword}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* デモ作成確認モーダル */}
      {showDemoCreateConfirm && (
        <div className="modal-overlay" onClick={handleCloseDemoCreateConfirm}>
          <div className="modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>デモアカウント作成の確認</h3>
              <button onClick={handleCloseDemoCreateConfirm} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '16px' }}>
                デモアカウントを <strong>{demoCreateCount || 1} 件</strong> 作成しますか？
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                クリニック名・アドレス・医師名は自動採番で作成されます。
              </p>
              {confirmActionError && (
                <div style={{ marginTop: '12px', color: '#dc2626', fontSize: '0.875rem' }}>
                  {confirmActionError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseDemoCreateConfirm} className="btn btn-secondary">
                キャンセル
              </button>
              <button onClick={handleConfirmDemoCreate} className="btn btn-primary" disabled={demoLoading}>
                {demoLoading ? <LoadingSpinner size="small" /> : '作成する'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  </div>
  );
};

export default AdminClinicList;
